import React, {useContext, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import PtActivityIndicator from "../core/pt-activity-indicator/pt-activity-indicator";
import {getTranslation} from "../../helpers/getTranslation";
import {View, StyleSheet, TouchableOpacity} from "react-native";
import PtText from "../core/pt-text/pt-text";
import {styleVars} from "../../app-style-vars";
import PtCallout from "../core/pt-callout/pt-callout";
import {useNavigation} from "@react-navigation/native";
import {GetTicketsRequest} from "../../model/GetTicketsRequest";
import {getTickets} from "../../actions/ticketsActions";
import PtTicketsTrackingSearchFilterWrapper from "./pt-tickets-tracking-search-filter-wrapper";
import PtTicketSingle from "./pt-ticket-single";
import UserContext from "../../helpers/UserContext";

const tomorrow = new Date().setDate(new Date().getDate() + 1);
const defaultMinimumDate = new Date(new Date(tomorrow).setFullYear(new Date(tomorrow).getFullYear() - 2)); // up to two years in the past
const defaultMaximumDate = new Date(); /** today */

export default function PtTicketsTrackingPage(props:any) {

  /** labels */
  const noResultsText = props.noResultsText || getTranslation('tickets.tracking.no.results.text') || 'No results are found. Please use the search for specific service request or filter accordingly';
  const noResultsTextLimited = props.noResultsTextLimited || getTranslation('tickets.tracking.limited.no.results.text') || 'No service requests found';
  const resultsFoundText = props.resultsFoundText || getTranslation('tickets.tracking.results.found.text') || 'results found';
  const seePortalMessage = props.seePortalMessage || getTranslation('tickets.tracking.see.portal.message') || 'More details needed? Look at our web portal';
  const errorMessage = props.errorMessage || getTranslation('tickets.tracking.orders.error.message') || 'Error occurred';
  const showAllTicketsText = props.showAllOrdersText || getTranslation('tickets.tracking.show.all.tickets.text') || 'Show all service requests';

  const limitedModeTitle = props.limitedModeTitle || getTranslation ('tickets.tracking.limited.mode.title') || 'Your latest service requests';
  const loadingLabel = props.loadingLabel || getTranslation ('tickets.tracking.loading.label') || 'Loading service requests';

  const limitedMode = props.limitedMode || false;
  const [limitedTickets, setLimitedTickets] = useState(null);
  const [limitedTicketsCount, setLimitedTicketsCount] = useState(null);

  const initialStartDate = props.startDate || new Date(new Date(tomorrow).setFullYear(new Date(tomorrow).getFullYear() - 1)); // year ago is default for tickets
  const initialEndDate = props.endDate || new Date(); // today
  const initialPageSize = props.pageSize || 20;

  const hideSearchAndFilters = props.hideSearchAndFilters || false;
  const hideLoadMore = props.hideLoadMore || false;

  const minimumDate = props.minimumDate || defaultMinimumDate;
  const maximumDate = props.maximumDate || defaultMaximumDate;

  const navigation = useNavigation();
  const {userData, updateUserData} = useContext(UserContext);

  const impersonationOK = !userData.mustImpersonate || (userData.mustImpersonate && userData.impersonatingId !== null);

  const searchOptions = [
    {value: 'protocolNumber', label: getTranslation('tickets.tracking.search.options.protocol.number')},
    {value: 'boschOrderNumber', label: getTranslation('tickets.tracking.search.options.bosch.order.number')},
    {value: 'boschInvoiceNumber', label: getTranslation('tickets.tracking.search.options.bosch.invoice.number')},
    {value: 'customerPONumber', label: getTranslation('tickets.tracking.search.options.customer.po.number')}
  ];

  const filtersOptions = {
    ticketStatus: {
      title: getTranslation('due.payments.filters.status.title'),
      choices: [
        {value: '1', label: getTranslation('tickets.tracking.filters.status.open')},
        {value: '2', label: getTranslation('tickets.tracking.filters.status.in.progress')},
        {value: '4', label: getTranslation('tickets.tracking.filters.status.customer.action')},
        {value: '5', label: getTranslation('tickets.tracking.filters.status.completed')},
        {value: '6', label: getTranslation('tickets.tracking.filters.status.closed')},
        {value: 'Z1', label: getTranslation('tickets.tracking.filters.status.external.action')},
      ]
    },
    ticketType: {
      title: getTranslation('due.payments.filters.type.title'),
      choices: [
        {value: '111', label: getTranslation('tickets.tracking.filters.type.orders')},
        {value: '121', label: getTranslation('tickets.tracking.filters.type.shipping')},
        {value: '101', label: getTranslation('tickets.tracking.filters.type.financials')},
        {value: '141', label: getTranslation('tickets.tracking.filters.type.other')},
      ]
    },
    ticketDate: {
      title: getTranslation('tickets.tracking.filters.date.title'),
      startDate: new Date(new Date(tomorrow).setFullYear(new Date(tomorrow).getFullYear() - 1)),
      minDate: defaultMinimumDate,
      endDate: new Date(),
      maxDate: new Date(),
      startDateLabel: getTranslation('tickets.tracking.filters.date.from.label'),
      endDateLabel: getTranslation('tickets.tracking.filters.date.to.label')
    }
  }

  const loadMoreLabel = props.loadMoreLabel || getTranslation('tickets.tracking.load.more.text');

  /** action related stuff */
  const PTBaseSiteUID = props.baseSiteUID || userData.baseSiteUID;
  const PTSalesAreaDependantCustomerUid = props.salesAreaDependantCustomerUid || userData.salesAreaDependantCustomerUid;
  const currentLanguage = useSelector((state:any) => state.translationsReducer.currentLanguage);
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state:any) => state.authReducer.isAuthenticated);
  const initialRequestSent = useSelector((state: any) => state.ticketsReducer.initialDataCollected)
  const loadingTickets = useSelector((state:any) => state.ticketsReducer.loading);
  const requestForTickets = useSelector((state:any) => state.ticketsReducer.request);
  const tickets = useSelector((state:any) => state.ticketsReducer.tickets);
  const ticketsCount = useSelector((state:any) => state.ticketsReducer.numberOfTickets);
  const errors = useSelector((state:any) => state.ticketsReducer.errors);

  /** keep all search and filtering parameters in a state at top level
   * and pass them down to other components as props
   * when something is changed in child components, pass all the way to the top
   * via handler functions in props, then change state on top level
   * it should propagate back down to child component via props
   */
  const requestInitialState = {
    pageSize: initialPageSize,
    offset: null,
    dateFrom: initialStartDate,
    dateTo: initialEndDate,
    ticketId: null,
    protocolNumber: null,
    boschOrderNumber: null,
    boschInvoiceNumber: null,
    customerPONumber: null,
    status: null,
    ticketType: null,
    qmNoteNumber: null,
    qmStatus: null,
    qmCustomerBook: null,
    qmAction: null,
    multiaccountSelected: null,
    searchType: 'protocolNumber',
    searchTerm: ''
  }
  const [requestParams, setRequestParams] = useState(requestInitialState);

  useEffect(() => {
    if(isAuthenticated && impersonationOK) {
      if (!initialRequestSent && tickets.length === 0) {
        const initialRequest = new GetTicketsRequest(initialPageSize, null, initialStartDate, initialEndDate);
        getTickets(dispatch, initialRequest, false, null, PTBaseSiteUID, PTSalesAreaDependantCustomerUid, currentLanguage)
          .then((response) => {
            if(limitedMode) {
              console.log('setting limitedTickets', response);
              setLimitedTickets(response?.tickets);
              setLimitedTicketsCount(response?.count)
            }
          })
          .finally(() => {
            console.log('the req after initial = ', requestForTickets);
          });
      }
    } else {
      setLimitedTickets(null);
    }
  },[initialRequestSent, isAuthenticated, impersonationOK])

  /** handlers for various props that have to be passed down the line and listened to back here */
  /** end date filter changed */
  const endDateChangedHandler = (data:any) => {
    console.log('tickets tracking page, endDateChangedHandler, new value = ', data.newDate);
    setRequestParams({...requestParams, dateTo: data.newDate});
  }
  /** start date filter changed */
  const startDateChangedHandler = (data:any) => {
    console.log('tickets tracking page, startDateChangedHandler, new value = ', data.newDate);
    setRequestParams({...requestParams, dateFrom: data.newDate});
  }
  /** ticket status filter changed */
  const ticketStatusFilterChangedHandler = (data:any) => {
    console.log('tickets trackin page, ticketStatusFilterChangedHandler, new value = ', data.newValue);
    setRequestParams({...requestParams, status: data.newValue})
  }
  /** ticket type filter changed */
  const ticketTypeFilterChangedHandler = (data:any) => {
    console.log('tickets tracking page, ticketTypeFilterChangedHandler, new value = ', data.newValue);
    setRequestParams({...requestParams, ticketType: data.newValue})
  }
  /** search type changed */
  const searchTypeChangedHandler = (data:any) => {
    console.log('tickets tracking page, searchTypeChangedHandler, new value = ', data.searchType);
    setRequestParams({...requestParams, searchType: data.searchType})
  }
  /** search term changed */
  const searchTermChangedHandler = (data:any) => {
    if(data.searchTerm == '') {data.searchTerm = null;}
    console.log('tickets tracking page, searchTermChangedHandler, new value = ', data.searchTerm)
    setRequestParams({...requestParams, searchTerm: data.searchTerm})
  }
  /** search initiated */
  const searchHandler = () => {
    /** search should set filters to initial values, time range to full span, page to initial, searchtype to searchterm and then send request */
    /** search request should not contain dates */
    setRequestParams({
      ...requestParams,
      dateFrom: minimumDate,
      dateTo: maximumDate,
      offset: null,
      status: null,
      ticketType: null,
      [requestParams.searchType]:requestParams.searchTerm.trim() === '' ? null : requestParams.searchTerm
    });
    const searchRequest = new GetTicketsRequest(initialPageSize, null );
    searchRequest[requestParams.searchType] = requestParams.searchTerm.trim() === '' ? null : requestParams.searchTerm;
    searchRequest.dateFrom = minimumDate;
    searchRequest.dateTo = maximumDate;
    getTickets(dispatch, searchRequest, false, null, PTBaseSiteUID, PTSalesAreaDependantCustomerUid, currentLanguage);
  }
  /** apply filters */
  const applyFiltersHandler = () => {
    /** apply filters should modify last request by setting date range, status, ticketType, search criteria and setting offset to null */
    setRequestParams({...requestParams, offset: null});
    const filterRequest = requestForTickets;
    filterRequest[requestParams.searchType] = requestParams.searchTerm.trim() === '' ? null : requestParams.searchTerm;
    filterRequest.dateFrom = requestParams.dateFrom;
    filterRequest.dateTo = requestParams.dateTo;
    filterRequest.status = requestParams.status;
    filterRequest.ticketType = requestParams.ticketType;
    filterRequest.offset = null;
    getTickets(dispatch, filterRequest, false, null, PTBaseSiteUID, PTSalesAreaDependantCustomerUid, currentLanguage);
  }
  /** reset filters and search */
  const resetHandler = () => {
    /** reset should trigger initial request*/
    setRequestParams(requestInitialState);
    const resetRequest = new GetTicketsRequest(initialPageSize, null);
    getTickets(dispatch, resetRequest, false, null, PTBaseSiteUID, PTSalesAreaDependantCustomerUid, currentLanguage);
  }

  /** load more */
  const loadMoreTickets = () => {
    const loadMoreRequest = requestForTickets;
    const newOffset = tickets.length;
    setRequestParams({...requestParams, offset: newOffset});
    loadMoreRequest.offset = newOffset;
    getTickets(dispatch, loadMoreRequest, true, tickets, PTBaseSiteUID, PTSalesAreaDependantCustomerUid, currentLanguage);
  }

  /** go to tickets page */
  const showAllTickets = () => {
    navigation.navigate('tickets');
  }

  return (
    <>
      {isAuthenticated ?
        <>
          <PtActivityIndicator show={loadingTickets} message={loadingLabel}/>
          <View style={{zIndex: 10}}>

            <PtTicketsTrackingSearchFilterWrapper
              hide = {hideSearchAndFilters}
              searchOptions={searchOptions}
              filtersOptions={filtersOptions}
              requestParams={requestParams}
              ticketStatusFilterChangedHandler={ticketStatusFilterChangedHandler}
              ticketTypeFilterChangedHandler={ticketTypeFilterChangedHandler}
              startDateChangedHandler={startDateChangedHandler}
              endDateChangedHandler={endDateChangedHandler}
              applyFiltersHandler={applyFiltersHandler}
              resetHandler={resetHandler}
              searchTermChangedHandler={searchTermChangedHandler}
              searchTypeChangedHandler={searchTypeChangedHandler}
              searchHandler={searchHandler}
              ticketsCount={ticketsCount}
            />
          </View>
          <View>
            {!limitedMode && !errors ?
              <View style={[styles.ordersCountWrapper]}>
                {ticketsCount == 0 ?
                  <View style={[styles.noResultsContainer]}>
                    <PtText style={[styles.noResultsText]}>{noResultsText}</PtText>
                  </View>
                  :
                  <>
                    <PtText style={[styles.ordersCountText]}>{ticketsCount} {resultsFoundText}</PtText>
                    <PtText style={[styles.seePortalMessageText]}>{seePortalMessage}</PtText>
                  </>
                }
              </View>
              : null
            }
            <View>
              { !limitedMode ?
                !errors ?
                  <>
                    {tickets?.map((ticket: any, idx: number) => (
                      <PtTicketSingle ticket={ticket} key={`single-ticket-${idx}`}></PtTicketSingle>
                    ))}
                  </>
                  :
                  <View>
                    <PtCallout type={'error'} message={errorMessage}/>
                  </View>
                :
                limitedMode ?
                  <>
                    <View>
                      <PtText style={[styles.limitedModeTitleText]}>{limitedModeTitle}</PtText>
                    </View>

                    {limitedTickets ?
                      <>
                        {limitedTickets?.map((ticket: any, idx: number) => (
                          idx < initialPageSize ?
                            <PtTicketSingle ticket={ticket} key={`single-ticket-${idx}`} limitedMode></PtTicketSingle>
                            : null
                        ))}
                      </>
                      : null
                    }

                    {limitedTicketsCount == 0 ?
                      <View style={[styles.noResultsContainer]}>
                        <PtText style={[styles.noResultsText]}>
                          {noResultsTextLimited}
                        </PtText>
                      </View>
                      : null
                    }

                    <View style={[styles.showAllContainer]}>
                      <TouchableOpacity onPress={() => showAllTickets()}>
                        <PtText style={[styles.showAllText]}>
                          {showAllTicketsText}
                        </PtText>
                      </TouchableOpacity>
                    </View>
                  </>
                  : null
              }
              {!hideLoadMore && !loadingTickets && (ticketsCount > requestParams.offset + requestParams.pageSize) ?
                <View style={[styles.loadMoreContainer]}>
                  <TouchableOpacity onPress={() => loadMoreTickets()}>
                    <View style={[styles.loadMoreButton]}>
                      <PtText style={[styles.loadMoreText]}>{loadMoreLabel}</PtText>
                    </View>
                  </TouchableOpacity>
                </View>
                : null
              }
            </View>
          </View>
        </>
        :
        <View>
          <PtText style={{fontSize:styleVars.rem(.625), textAlign:'center'}}>{'Feature not available. Please login to view your service requests.'}</PtText>
        </View>
      }
    </>
  )
}

const styles = StyleSheet.create({
  limitedModeTitleText: {
    fontWeight: '600',
    fontSize: styleVars.rem(1.25),
  },
  orderHeaderWrapper: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  orderNoWrapper: {
    flex: 1,
    maxWidth: '30%',
    borderWidth: 1,
    borderColor: styleVars.boschLightGray
  },
  orderDateWrapper: {
    flex: 1,
    maxWidth: '30%',
    borderWidth: 1,
    borderColor: styleVars.boschLightGray
  },
  orderStatusWrapper: {
    flex: 1,
    maxWidth: '30%',
    borderWidth: 1,
    borderColor: styleVars.boschLightGray
  },
  loadMoreContainer: {
    justifyContent: 'center',
    width: '100%',
  },
  loadMoreButton: {
    marginVertical: styleVars.rem(.5),
    paddingVertical: styleVars.rem(.5),
    paddingHorizontal: styleVars.rem(1),
    borderWidth: 1,
    borderColor: styleVars.boschDarkBlue,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
    flexGrow: 0
  },
  loadMoreText: {
    color: styleVars.boschDarkBlue
  },
  ordersCountWrapper: {
    paddingVertical: styleVars.rem(.5),
  },
  ordersCountText: {
    fontSize: styleVars.rem(.875),
    fontWeight: '600',
  },
  seePortalMessageText: {
    fontSize: styleVars.rem(.875),
    color: styleVars.boschLightGray,
  },
  noResultsContainer: {
    paddingHorizontal: styleVars.rem(.5),
    paddingVertical: styleVars.rem(.5),
  },
  noResultsText: {
    fontSize: styleVars.rem(.875),
    textAlign: 'justify'
  },
  showAllContainer: {
    paddingVertical: styleVars.rem(.5),
    paddingHorizontal: styleVars.rem(.5),
  },
  showAllText: {
    fontSize: styleVars.rem(.875),
    color: styleVars.boschDarkBlue,
    textAlign: 'right',
  }
})
